import { makeStyles } from '@material-ui/core/styles'
import {
  coachProfileBandOuter,
  coachProfileBandSectionGap,
  coachProfileContentCanvas,
  coachProfileSectionHeading,
} from '@lib/pga-coach/coach-profile-layout'

const useStyles = makeStyles((theme) => ({
  /**
   * Full-bleed white photos band. Breaks out to the viewport so the
   * white surface runs edge-to-edge, matching the breakout used by
   * `CoachProfileTestimonials` on this page.
   * Horizontal padding lives on this wrapper; `photosSectionContainer`
   * is the shared 1200px editorial canvas (no inner horizontal inset).
   */
  testimonialsWrapper: ({ inColumn }) => ({
    /**
     * Band-level top gap is `coachProfileBandSectionGap` (48px) — the
     * Photos→Testimonials stacked space. Padding bottom stays 0 so the
     * next band can own the same gap without doubling.
     */
    backgroundColor: theme.palette.common.white,
    color: theme.palette.grey.darkest,
    ...coachProfileBandOuter(theme, {
      inColumn,
      fullBleed: !inColumn,
      ...coachProfileBandSectionGap(theme),
      paddingBottom: 0,
    }),
  }),
  photosSectionContainer: ({ inColumn }) => ({
    ...coachProfileContentCanvas({ inColumn }),
    padding: 0,
  }),
  photosDesktopInner: {
    width: '100%',
    maxWidth: 'none',
    marginLeft: 0,
    marginRight: 0,
  },
  /**
   * Section headline. Same computed type as other flagged band titles
   * (`coachProfileSectionHeading`: visual h5 / semantic h2).
   */
  sectionHeading: {
    ...coachProfileSectionHeading(theme),
    marginBottom: theme.spacing(3),
    textAlign: 'left',
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(4),
    },
  },
  sectionHeadingMobile: {
    textAlign: 'center',
    width: '100%',
  },
  photosGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: theme.spacing(2.5),
    },
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: theme.spacing(2.5),
    },
  },
  photoGridCell: {
    borderRadius: 12,
    overflow: 'hidden',
    lineHeight: 0,
    boxShadow: '0 4px 24px rgba(0, 35, 75, 0.08)',
    backgroundColor: theme.palette.grey.light,
  },
  photoGridTrigger: {
    display: 'block',
    width: '100%',
    margin: 0,
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    borderRadius: 12,
    lineHeight: 0,
    textAlign: 'left',
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  gridPhoto: {
    width: '100%',
    height: '100%',
    display: 'block',
    objectFit: 'cover',
    aspectRatio: '4 / 3',
    [theme.breakpoints.down('sm')]: {
      aspectRatio: '16 / 10',
    },
  },
  lightboxPaper: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    margin: theme.spacing(1),
    padding: 0,
    maxWidth: 'calc(100vw - 16px)',
    maxHeight: 'calc(100vh - 16px)',
    width: 'max-content',
    overflow: 'visible',
  },
  lightboxRoot: {
    position: 'relative',
    display: 'inline-block',
    margin: 0,
    padding: 0,
    lineHeight: 0,
    verticalAlign: 'top',
  },
  lightboxImage: {
    display: 'block',
    maxWidth: 'min(92vw, 800px)',
    maxHeight: 'min(85vh, 600px)',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
    borderRadius: 12,
  },
  /**
   * Lightbox chrome (X + prev/next arrows) matches the header desktop
   * navigation drawer's `Close Menu` IconButton — a plain MUI
   * `IconButton` with no surface, border, or shadow, just the icon
   * glyph centered in the default 42px hit-target.
   *
   * Cross-background legibility uses the classic photo-viewer pattern
   * rather than `mix-blend-mode: difference`. The blend approach was
   * broken in practice because the IconButton creates its own
   * stacking context (`position: absolute` + `z-index`), so the SVG
   * could only blend within that empty context, not with the photo
   * behind. Instead:
   *
   *  - `color: white` so the icon paints white (max contrast on dark
   *    photos)
   *  - `& path { stroke-width: 2 }` overrides the `IconClose` 1px
   *    hairline so the X reads cleanly over photo noise
   *  - `filter: drop-shadow(...)` paints a dark halo around every
   *    icon stroke so the white icon stays readable on light photos
   *    (PGA REACH promo banners, snow, sky) — the halo provides the
   *    contrast a plain bright stroke can't carry on a bright bg.
   *
   * Header consumers of `IconClose` are unaffected because none of
   * the above is applied at the icon-component level — only here on
   * the photo-viewer buttons where varying backgrounds demand it.
   */
  lightboxClose: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 2,
    color: theme.palette.common.white,
    '& svg': {
      filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.85)) drop-shadow(0 0 4px rgba(0, 0, 0, 0.55))',
    },
    '& path': {
      strokeWidth: 2,
    },
  },
  lightboxNav: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    color: theme.palette.common.white,
    '& svg': {
      filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.85)) drop-shadow(0 0 4px rgba(0, 0, 0, 0.55))',
    },
    '& path': {
      strokeWidth: 2,
    },
  },
  /**
   * `IconButtonCtaArrow` points right by default — rotate it 180° on
   * the prev button so a single SVG covers both directions, mirroring
   * how `SliderControl.left` flips the same icon for the slider
   * carousel.
   */
  lightboxNavArrow: {
    transform: 'rotate(180deg)',
  },
  lightboxNavPrev: {
    left: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      left: theme.spacing(0.5),
    },
  },
  lightboxNavNext: {
    right: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      right: theme.spacing(0.5),
    },
  },
  mobileSection: ({ inColumn }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    ...coachProfileBandSectionGap(theme),
    paddingLeft: inColumn ? 0 : theme.spacing(2),
    paddingRight: inColumn ? 0 : theme.spacing(2),
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    position: 'relative',
    boxSizing: 'border-box',
    overflow: 'hidden',
    ...(inColumn ? null : {
      left: '50%',
      transform: 'translateX(-50%)',
      width: '100vw',
      maxWidth: '100vw',
    }),
  }),
  mobileSliderShell: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
  },
  mobileKeenSlider: {
    overflow: 'hidden !important',
    padding: `${theme.spacing(2)} 0 ${theme.spacing(1)}`,
    '& .keen-slider__slide': {
      overflow: 'hidden !important',
      minWidth: '0 !important',
      maxWidth: '100% !important',
      width: '100% !important',
      flex: '0 0 100% !important',
    },
    '& [class*="slideScaler"]': {
      scale: '1 !important',
    },
  },
  slideWrapper: {
    paddingBottom: theme.spacing(1),
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  },
  slideImage: {
    width: '100%',
    maxWidth: '100%',
    height: 'auto',
    borderRadius: 12,
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    boxSizing: 'border-box',
  },
  customControl: {},
}))

export default useStyles
